/**
 * Script detection and properties for language families
 * This provides script-based settings rather than language-specific hardcoding
 */

export type Script = 
  | 'Latin'
  | 'Cyrillic'
  | 'Arabic'
  | 'Hebrew'
  | 'Devanagari'
  | 'Bengali'
  | 'Gurmukhi'
  | 'Tamil'
  | 'Telugu'
  | 'Kannada'
  | 'Malayalam'
  | 'Thai'
  | 'Georgian'
  | 'Ethiopic'
  | 'CJK'; // Chinese, Japanese, Korean

export interface ScriptProperties {
  name: Script;
  direction: 'ltr' | 'rtl';
  fontSizeMultiplier: number;
  lineHeightMultiplier: number;
  textAreaHeightMultiplier: number;
  requiresComplexShaping: boolean;
  // Font fallback patterns for this script
  fontPathPatterns: string[];
}

// Script mapping by language code
const LANGUAGE_TO_SCRIPT: Record<string, Script> = {
  // Latin script
  'en': 'Latin', 'es': 'Latin', 'fr': 'Latin', 'de': 'Latin',
  'it': 'Latin', 'pt': 'Latin', 'nl': 'Latin', 'sv': 'Latin',
  'da': 'Latin', 'no': 'Latin', 'fi': 'Latin',
  
  // Cyrillic script
  'ru': 'Cyrillic', 'uk': 'Cyrillic', 'bg': 'Cyrillic', 'sr': 'Cyrillic',
  
  // CJK
  'zh': 'CJK', 'ja': 'CJK', 'ko': 'CJK',
  'zh-CN': 'CJK', 'zh-TW': 'CJK',
  
  // Arabic script (RTL)
  'ar': 'Arabic', 'fa': 'Arabic', 'ur': 'Arabic',
  
  // Hebrew script (RTL)
  'he': 'Hebrew', 'yi': 'Hebrew',
  
  // Indic scripts
  'hi': 'Devanagari', 'mr': 'Devanagari', 'ne': 'Devanagari',
  'bn': 'Bengali', 'as': 'Bengali',
  'pa': 'Gurmukhi',
  'ta': 'Tamil',
  'te': 'Telugu',
  'kn': 'Kannada',
  'ml': 'Malayalam',
  
  // Other scripts
  'th': 'Thai',
  'ka': 'Georgian',
  'am': 'Ethiopic'
};

// Script properties configuration
const SCRIPT_PROPERTIES: Record<Script, ScriptProperties> = {
  Latin: {
    name: 'Latin',
    direction: 'ltr',
    fontSizeMultiplier: 1.0,
    lineHeightMultiplier: 1.0,
    textAreaHeightMultiplier: 1.0,
    requiresComplexShaping: false,
    fontPathPatterns: ['noto-sans/NotoSans', 'Arial', 'Helvetica']
  },
  
  Cyrillic: {
    name: 'Cyrillic',
    direction: 'ltr',
    fontSizeMultiplier: 0.95,
    lineHeightMultiplier: 1.0,
    textAreaHeightMultiplier: 1.0,
    requiresComplexShaping: false,
    fontPathPatterns: ['noto-sans/NotoSans', 'Arial']
  },
  
  CJK: {
    name: 'CJK',
    direction: 'ltr',
    fontSizeMultiplier: 0.9,
    lineHeightMultiplier: 1.2,
    textAreaHeightMultiplier: 1.1,
    requiresComplexShaping: false,
    fontPathPatterns: ['noto-sans-cjk/NotoSansCJK', 'SimSun', 'MS Gothic']
  },
  
  Arabic: {
    name: 'Arabic',
    direction: 'rtl',
    fontSizeMultiplier: 0.95,
    lineHeightMultiplier: 1.3,
    textAreaHeightMultiplier: 1.2,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-arabic/NotoSansArabic', 'Arial']
  },
  
  Hebrew: {
    name: 'Hebrew',
    direction: 'rtl',
    fontSizeMultiplier: 0.95,
    lineHeightMultiplier: 1.1,
    textAreaHeightMultiplier: 1.1,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-hebrew/NotoSansHebrew', 'Arial']
  },
  
  Devanagari: {
    name: 'Devanagari',
    direction: 'ltr',
    fontSizeMultiplier: 1.0, // Increased for better visibility
    lineHeightMultiplier: 1.5, // Adjusted for better line spacing
    textAreaHeightMultiplier: 1.3,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-devanagari/NotoSansDevanagari', 'NotoSansDevanagari', 'noto-sans/NotoSans']
  },
  
  Bengali: {
    name: 'Bengali',
    direction: 'ltr',
    fontSizeMultiplier: 0.9,
    lineHeightMultiplier: 1.4,
    textAreaHeightMultiplier: 1.3,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-bengali/NotoSansBengali', 'noto-sans/NotoSans']
  },
  
  Gurmukhi: {
    name: 'Gurmukhi',
    direction: 'ltr',
    fontSizeMultiplier: 0.9,
    lineHeightMultiplier: 1.4,
    textAreaHeightMultiplier: 1.3,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-gurmukhi/NotoSansGurmukhi', 'noto-sans/NotoSans']
  },
  
  Tamil: {
    name: 'Tamil',
    direction: 'ltr',
    fontSizeMultiplier: 0.95, // Increased for better visibility
    lineHeightMultiplier: 1.7, // Adjusted for better line spacing
    textAreaHeightMultiplier: 1.45, // Increased for preventing text overflow
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-tamil/NotoSansTamil', 'noto-sans/NotoSans']
  },
  
  Telugu: {
    name: 'Telugu',
    direction: 'ltr',
    fontSizeMultiplier: 0.9,
    lineHeightMultiplier: 1.5,
    textAreaHeightMultiplier: 1.3,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-telugu/NotoSansTelugu', 'noto-sans/NotoSans']
  },
  
  Kannada: {
    name: 'Kannada',
    direction: 'ltr',
    fontSizeMultiplier: 0.9,
    lineHeightMultiplier: 1.5,
    textAreaHeightMultiplier: 1.3,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-kannada/NotoSansKannada', 'noto-sans/NotoSans']
  },
  
  Malayalam: {
    name: 'Malayalam',
    direction: 'ltr',
    fontSizeMultiplier: 0.95, // Increased for better visibility
    lineHeightMultiplier: 1.7, // Adjusted for better line spacing
    textAreaHeightMultiplier: 1.45, // Increased for preventing text overflow
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-malayalam/NotoSansMalayalam', 'noto-sans/NotoSans']
  },
  
  Thai: {
    name: 'Thai',
    direction: 'ltr',
    fontSizeMultiplier: 0.9,
    lineHeightMultiplier: 1.5,
    textAreaHeightMultiplier: 1.3,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-thai/NotoSansThai', 'noto-sans/NotoSans']
  },
  
  Georgian: {
    name: 'Georgian',
    direction: 'ltr',
    fontSizeMultiplier: 0.95,
    lineHeightMultiplier: 1.2,
    textAreaHeightMultiplier: 1.1,
    requiresComplexShaping: false,
    fontPathPatterns: ['noto-sans-georgian/NotoSansGeorgian', 'noto-sans/NotoSans']
  },
  
  Ethiopic: {
    name: 'Ethiopic',
    direction: 'ltr',
    fontSizeMultiplier: 0.9,
    lineHeightMultiplier: 1.4,
    textAreaHeightMultiplier: 1.2,
    requiresComplexShaping: true,
    fontPathPatterns: ['noto-sans-ethiopic/NotoSansEthiopic', 'noto-sans/NotoSans']
  }
};

/**
 * Detect script from language code
 */
export function detectScript(languageCode: string): Script {
  // Try exact match first
  if (LANGUAGE_TO_SCRIPT[languageCode]) {
    return LANGUAGE_TO_SCRIPT[languageCode];
  }
  
  // Try base language (e.g., 'en-US' -> 'en')
  const baseLanguage = languageCode.split('-')[0];
  if (LANGUAGE_TO_SCRIPT[baseLanguage]) {
    return LANGUAGE_TO_SCRIPT[baseLanguage];
  }
  
  // Default to Latin
  return 'Latin';
}

/**
 * Get script properties for a language
 */
export function getScriptProperties(languageCode: string): ScriptProperties {
  const script = detectScript(languageCode);
  return SCRIPT_PROPERTIES[script];
}

/**
 * Check if a script requires RTL layout
 */
export function isRTL(languageCode: string): boolean {
  const props = getScriptProperties(languageCode);
  return props.direction === 'rtl';
}

/**
 * Get font adjustment factors for a language
 */
export function getFontAdjustments(languageCode: string) {
  const props = getScriptProperties(languageCode);
  return {
    fontSize: props.fontSizeMultiplier,
    lineHeight: props.lineHeightMultiplier,
    textAreaHeight: props.textAreaHeightMultiplier
  };
}

/**
 * Get font path patterns for a script
 */
export function getFontPathPatterns(languageCode: string): string[] {
  const props = getScriptProperties(languageCode);
  return props.fontPathPatterns;
}

/**
 * Check if script requires complex text shaping
 */
export function requiresComplexShaping(languageCode: string): boolean {
  const props = getScriptProperties(languageCode);
  return props.requiresComplexShaping;
}
